import apiClient from './client';

export interface LensWeights {
  wgtCost: number;
  wgtLabour: number;
  wgtRemit: number;
  wgtSROI: number;
  wgtSystemic: number;
  wgtClimate: number;
  wgtRisk: number;
}

export interface StakeholderLens {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  weights?: LensWeights;
  cluster_count?: number;
  avg_weight?: number;
  analysis?: {
    weight_validation: {
      is_valid: boolean;
      errors: string[];
      warnings: string[];
      total_weight: number;
    };
    recommendations: Array<{
      cluster: string;
      message: string;
      severity: string;
    }>;
    recommendation_severity: string;
  };
}

export interface CreateLensRequest {
  name: string;
  description: string;
  weights: LensWeights;
}

export interface UpdateLensRequest {
  name?: string;
  description?: string;
}

export interface LensComparison {
  lens1: StakeholderLens;
  lens2: StakeholderLens;
  weight_differences: {
    [key: string]: {
      lens1: number;
      lens2: number;
      difference: number;
    };
  };
  similarity_score: number;
  recommendations: string[];
}

export interface LensImpactPreview {
  lens: StakeholderLens;
  preview: {
    affected_metrics: number;
    affected_clusters: number;
    total_impact_change: number;
    cluster_impacts: Array<{
      cluster_id: string;
      cluster_name: string;
      current_impact: number;
      new_impact: number;
      change_percentage: number;
    }>;
  };
}

export interface LensWeightSummary {
  lens_id: number;
  lens_name: string;
  cluster_weights: Array<{
    cluster_name: string;
    weight: number;
    percentage: number;
  }>;
  total_weight: number;
}

class LensesApi {
  // Get all lenses with optional filtering
  async getLenses(params?: {
    active_only?: boolean;
    search?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
  }): Promise<StakeholderLens[]> {
    try {
      const response = await apiClient.getRaw<StakeholderLens[]>(`/lenses`, {
        params,
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch lenses:', error);
      throw error;
    }
  }

  // Get a specific lens by ID
  async getLensById(id: number): Promise<StakeholderLens> {
    try {
      const response = await apiClient.getRaw<StakeholderLens>(`/lenses/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch lens by ID:', error);
      throw error;
    }
  }

  // Create a new lens
  async createLens(data: CreateLensRequest): Promise<StakeholderLens> {
    try {
      return await apiClient.post<StakeholderLens>(`/lenses`, data);
    } catch (error) {
      console.error('Failed to create lens:', error);
      throw error;
    }
  }

  // Update lens basic information
  async updateLens(id: number, data: UpdateLensRequest): Promise<StakeholderLens> {
    try {
      return await apiClient.put<StakeholderLens>(`/lenses/${id}`, data);
    } catch (error) {
      console.error('Failed to update lens:', error);
      throw error;
    }
  }

  // Update lens weights
  async updateLensWeights(id: number, weights: LensWeights): Promise<StakeholderLens> {
    try {
      return await apiClient.put<StakeholderLens>(`/lenses/${id}/weights`, {
        weights,
      });
    } catch (error) {
      console.error('Failed to update lens weights:', error);
      throw error;
    }
  }

  // Delete (deactivate) a lens
  async deleteLens(id: number): Promise<void> {
    try {
      await apiClient.delete(`/lenses/${id}`);
    } catch (error) {
      console.error('Failed to delete lens:', error);
      throw error;
    }
  }

  // Preview lens impact
  async previewLensImpact(id: number, params?: {
    scenario_id?: number;
    country_id?: number;
  }): Promise<LensImpactPreview> {
    try {
      const response = await apiClient.getRaw<LensImpactPreview>(`/lenses/${id}/preview`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to preview lens impact:', error);
      throw error;
    }
  }

  // Apply lens to data
  async applyLens(id: number, data?: {
    scenario_id?: number;
    country_id?: number;
    apply_to?: string;
  }): Promise<{
    lens_id: number;
    applied_at: string;
    affected_records: number;
    status: string;
  }> {
    try {
      return await apiClient.post<{
        lens_id: number;
        applied_at: string;
        affected_records: number;
        status: string;
      }>(`/lenses/${id}/apply`, data || {});
    } catch (error) {
      console.error('Failed to apply lens:', error);
      throw error;
    }
  }

  // Get lens weight summary
  async getLensWeightSummary(id: number): Promise<LensWeightSummary> {
    try {
      const response = await apiClient.getRaw<LensWeightSummary>(`/lenses/${id}/weights/summary`);
      return response.data;
    } catch (error) {
      console.error('Failed to get lens weight summary:', error);
      throw error;
    }
  }

  // Compare two lenses
  async compareLenses(id1: number, id2: number): Promise<LensComparison> {
    try {
      const response = await apiClient.getRaw<LensComparison>(`/lenses/compare/${id1}/${id2}`);
      return response.data;
    } catch (error) {
      console.error('Failed to compare lenses:', error);
      throw error;
    }
  }

  // Validate weights without saving
  validateWeights(weights: LensWeights): {
    is_valid: boolean;
    errors: string[];
    warnings: string[];
    total: number;
  } {
    const total = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if total is approximately 1
    if (Math.abs(total - 1) > 0.01) {
      errors.push(`Weights must sum to 100%. Current sum: ${(total * 100).toFixed(1)}%`);
    }

    // Check for negative weights
    Object.entries(weights).forEach(([key, value]) => {
      if (value < 0) {
        errors.push(`${this.getClusterName(key)} cannot have negative weight`);
      }
      if (value === 0) {
        warnings.push(`${this.getClusterName(key)} has zero weight`);
      }
      if (value > 0.5) {
        warnings.push(`${this.getClusterName(key)} has very high weight (${(value * 100).toFixed(1)}%)`);
      }
    });

    return {
      is_valid: errors.length === 0,
      errors,
      warnings,
      total,
    };
  }

  private getClusterName(key: string): string {
    const names: { [key: string]: string } = {
      wgtCost: "Cost Efficiency",
      wgtLabour: "Labour & Employment",
      wgtRemit: "Remittances & Financial Inclusion",
      wgtSROI: "Social Return on Investment",
      wgtSystemic: "Systemic Impact",
      wgtClimate: "Climate & Environment",
      wgtRisk: "Risk Mitigation",
    };
    return names[key] || key;
  }
}

const lensesApi = new LensesApi();
export default lensesApi;